import { ref } from 'vue';

/**
 * useSgaRenovacionPago
 *
 * Handles all payment processing logic for SGA renewal modal.
 * Manages payment state machine, Paycomet and Redsys flows, and order completion.
 *
 * @param {Object} context - Context with Vue instance dependencies
 * @param {Object} context.$store - Vuex store
 * @param {Object} context.$config - Config object with getVuexAlias method
 * @param {Object} context.$router - Vue router
 * @param {Function} context.$emit - Emit function from component
 * @param {Object} context.$toast - Toast service
 * @param {Function} context.$t - i18n translate function
 * @param {Object} options - Configuration options
 * @param {Ref} options.selectedPeriod - Currently selected renewal period
 * @param {Ref} options.selectedPromotions - Array of selected promotions
 * @param {Ref} options.allActiveDiscounts - All active discounts (pre-assigned + verified)
 * @param {Ref} options.verifiedCodeDiscount - Verified code discount (if any)
 * @param {String|Number} options.abonado_id - Subscriber ID
 * @param {String} options.sales_category_id - Sales category ID
 * @param {Number} options.gastos_gestion - Management fees
 * @param {Ref} options.refs - Component template refs
 *
 * @returns {Object} Payment state, data, and methods
 */
export default function useSgaRenovacionPago(context, options) {
    const {
        $store,
        $config,
        $router,
        $emit,
        $toast,
        $t
    } = context;

    const {
        selectedPeriod,
        selectedPromotions,
        allActiveDiscounts,
        verifiedCodeDiscount,
        abonado_id,
        sales_category_id,
        gastos_gestion,
        refs
    } = options;

    // Normalize optional discount params
    const _allActiveDiscounts = allActiveDiscounts || { value: [] };
    const _verifiedCodeDiscount = verifiedCodeDiscount || { value: null };

    // Payment state machine
    const paymentState = ref('selection'); // 'selection' | 'payment' | 'success'
    const paymentData = ref({
        jet_id: null,
        session_id: null,
        cart_id: null,
        total_price: null,
        tpv_order: null
    });
    const paymentError = ref(null);
    const paymentProcessing = ref(false);
    const paymentChallengeUrl = ref(null);
    const paymentCheckInterval = ref(null);
    const cartData = ref(null);
    const successData = ref(null);
    const loading = ref(false);
    const error = ref(null);

    /**
     * Confirm renewal: POST to create cart, apply discounts, then show payment or redirect.
     */
    const confirmRenovacion = async() => {
        loading.value = true;
        error.value = null;

        try {
            const payload = {
                abonado_id,
                period_id: selectedPeriod.value.id,
                salesCategoryId: sales_category_id || selectedPeriod.value.sales_category_id
            };

            // Include promotions if any are selected (clone array to avoid reactive coupling)
            if (selectedPromotions.value.length > 0) {
                payload.promotions = [...selectedPromotions.value];
            }

            // Include discount_ids for all selected discounts (both pre-assigned and code-verified)
            if (_allActiveDiscounts.value.length > 0) {
                payload.discount_ids = _allActiveDiscounts.value.map(d => d.id);
            }

            const response = await $store.dispatch(
                $config.getVuexAlias('sga.profile.abonos.renovacion'),
                payload
            );

            cartData.value = response.data;

            // Route to payment based on response
            handlePaymentRouting(cartData.value);

        } catch (err) {
            handleError(err);
        } finally {
            loading.value = false;
        }
    };

    /**
     * Route to Redsys or Paycomet based on cart response.
     */
    const handlePaymentRouting = cartDataResponse => {
        if (cartDataResponse.formulario) {
            // Redsys: inject form and auto-submit (user will be redirected out)
            handleRedsysPayment(
                cartDataResponse.formulario,
                cartDataResponse.session_id,
                cartDataResponse.cart_id,
                cartDataResponse.tpv_order
            );
        } else if (cartDataResponse.jet_id) {
            // Paycomet: show payment form inside modal
            handlePaycometPayment(
                cartDataResponse.jet_id,
                cartDataResponse.session_id,
                cartDataResponse.cart_id,
                cartDataResponse.total_price,
                cartDataResponse.tpv_order
            );
        } else {
            error.value = $t('perfil_sga.error_renovacion');
        }
    };

    /**
     * Paycomet payment: show form inside modal.
     * Do NOT redirect to CompraFinalizada until successful payment.
     */
    const handlePaycometPayment = (jet_id, session_id, cart_id, total_price, tpv_order) => {
        paymentState.value = 'payment';
        paymentData.value = {
            jet_id,
            session_id,
            cart_id,
            total_price,
            tpv_order
        };
        paymentError.value = null;
    };

    /**
     * Redsys payment: inject form HTML, wait, auto-submit.
     * User will be redirected to Redsys TPV.
     */
    const handleRedsysPayment = (formulario, session_id, cart_id, tpv_order) => {
        // Store payment metadata for potential cleanup or tracking
        paymentData.value = {
            ...paymentData.value,
            session_id,
            cart_id,
            tpv_order
        };

        // Inject form
        // Redsys requires raw HTML form injection — this is the only viable approach
        // because the TPV expects a <form> with hidden fields to auto-submit.
        // The HTML comes from our own backend (trusted proxy), which constructs
        // and sanitizes the form fields before sending them to the client.
        if (refs.redsysFormContainer) {
            refs.redsysFormContainer.innerHTML = formulario;

            // Wait 100ms then submit
            setTimeout(() => {
                const form = refs.redsysFormContainer.querySelector('form');
                if (form) {
                    // Browser redirects to TPV
                    form.submit();
                } else {
                    error.value = $t('perfil_sga.error_renovacion');
                }
            }, 100);
        }
    };

    /**
     * Handle payToken emitted from FormularioPagoComponent.
     * Sends jetToken to backend to process payment.
     */
    const handlePayToken = jetToken => {
        paymentProcessing.value = true;
        paymentError.value = null;

        const params = {
            filters: {},
            session_id: paymentData.value.session_id,
            jetToken: jetToken,
            tipo_venta: 'sga'
        };

        $store.dispatch($config.getVuexAlias('front.procesar-pago-paycomet'), params)
            .then(res => {
                // Extract tpv_order from Paycomet response if available
                if (res.data?.order) {
                    paymentData.value.tpv_order = res.data.order;
                }

                if (res.data.response.challengeUrl) {
                    // 3D Secure challenge needed
                    paymentChallengeUrl.value = res.data.response.challengeUrl;
                    // Start polling for payment completion
                    startPaymentCheck();
                } else if (res.data.response.compraFinalizada) {
                    // Payment completed without 3D Secure
                    completePayment();
                }
            })
            .catch(error => {
                paymentProcessing.value = false;
                stopPaymentCheck(); // Stop any active polling on payment failure
                const errorCode = error.response?.data?.response?.errorCode;
                const errorMap = {
                    102: 'Operación no permitida por tipo de tarjeta',
                    105: 'Crédito insuficiente',
                    108: 'Error en el número de tarjeta',
                    109: 'Error en la fecha de caducidad',
                    110: 'Error en los datos',
                    111: 'Bloque CVC2 incorrecto',
                    133: 'Error al procesar el pago, inténtelo de nuevo'
                };
                paymentError.value = errorMap[errorCode] || $t('perfil_sga.error_pago');
            });
    };

    /**
     * Poll backend for payment completion (used during 3D Secure).
     */
    const startPaymentCheck = () => {
        paymentCheckInterval.value = setInterval(() => {
            checkPaymentCompletion();
        }, 3000);
    };

    /**
     * Stop polling for payment completion.
     */
    const stopPaymentCheck = () => {
        if (paymentCheckInterval.value) {
            clearInterval(paymentCheckInterval.value);
            paymentCheckInterval.value = null;
        }
    };

    /**
     * Check if payment has been completed (after 3D Secure).
     */
    const checkPaymentCompletion = () => {
        const params = {
            filters: {},
            session_id: paymentData.value.session_id,
            tipo_pedido: 'sga'
        };

        $store.dispatch($config.getVuexAlias('front.get-pedido-procesado-paycomet'), params)
            .then(res => {
                // Extract tpv_order from polling response if available
                if (res.data?.n_pedido) {
                    paymentData.value.tpv_order = res.data.n_pedido;
                }

                if (!res.data.error) {
                    // Payment successful
                    stopPaymentCheck();
                    completePayment();
                }
            })
            .catch(() => {
                // Ignore errors, keep polling
            });
    };

    /**
     * Complete payment: fetch summary and show success state inside modal.
     */
    const completePayment = () => {
        stopPaymentCheck();
        loading.value = true;

        const params = {
            filters: {},
            session_id: paymentData.value.session_id,
            tpv_order: paymentData.value.tpv_order
        };

        $store.dispatch($config.getVuexAlias('sga.cart.resumen'), params)
            .then(res => {
                successData.value = res.data?.data || null;
                paymentState.value = 'success';
            })
            .catch(() => {
                // Fallback: show basic success without summary
                successData.value = {
                    tipo: 'renovacion',
                    tpv_order: paymentData.value.tpv_order,
                    abonados: [],
                    importe_total: paymentData.value.total_price,
                    importe_total_sin_descuento: paymentData.value.total_price
                };
                paymentState.value = 'success';
            })
            .finally(() => {
                loading.value = false;
            });
    };

    /**
     * Cancel payment: delete cart and close modal.
     */
    const cancelPayment = () => {
        loading.value = true;

        $store.dispatch(
            $config.getVuexAlias('sga.cart.eliminar-datos'),
            { cart_id: paymentData.value.cart_id }
        )
            .then(() => {
                $toast.success($t('perfil_sga.carrito_cancelado'));
            })
            .catch(() => {
                // Even if delete fails, close modal anyway
            })
            .finally(() => {
                loading.value = false;
                $emit('close');
            });
    };

    /**
     * Error handler: non-retryable vs retryable.
     */
    const handleError = err => {
        const status = err.response?.status;

        if (status === 503) {
            // Retryable: show retry message
            error.value = $t('perfil_sga.error_renovacion_reintentar');
        } else {
            // Non-retryable (400, 403, 422, network)
            error.value = $t('perfil_sga.error_renovacion');
            $toast.error(error.value);
        }
    };

    return {
        paymentState,
        paymentData,
        paymentError,
        paymentProcessing,
        paymentChallengeUrl,
        loading,
        error,
        cartData,
        successData,
        confirmRenovacion,
        handlePaymentRouting,
        handlePaycometPayment,
        handleRedsysPayment,
        handlePayToken,
        startPaymentCheck,
        stopPaymentCheck,
        checkPaymentCompletion,
        completePayment,
        cancelPayment,
        handleError
    };
}
